import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { uiZoom, layoutViewport } from "../scale.js";

/* The bell — RepIQ's in-app notifications (Salem 2026-08-25, in-app ONLY by decision).
   Lives in the sidebar on every page. Polls /api/notifications once a minute (paused
   while the tab is hidden — no point ringing an empty room), badge shows unread count,
   the panel lists the latest and marks-read on click. Kinds and copy come from the
   server; this component just shows them and navigates. */

const POLL_MS = 60_000;
const ORDER_NOTICE = new Set(["order_cancelled", "order_sov_changed"]);   // acknowledged on the Today page

const KIND_ICON = {
  lead_assigned: "📥", callback_morning: "📞", callback_soon: "⏰",
  acceptance_overdue: "⚠️", acceptance_overdue_mgr: "⚠️",
  lead_no_answer: "📵", lead_dormant: "💤", importance_set: "⭐", lead_rejected: "🚫",
  review_due: "🗓", probation_review_due: "📋",
  rejection_requested: "🚫", rejection_declined: "↪",
  lead_bc_notice: "⏳", lead_reignited: "🔥", lead_cancelled: "✖", lead_reinstated: "↩",
  lead_rebook_clock: "⏱", lead_ai_rebooked: "📅", order_cancelled: "✖", order_sov_changed: "💷",
  callback_heard: "👂", callback_missed: "🚨",
  forecast_repeat: "🔁",
  security: "🔐",
  outage: "⚡",
  tender: "📑",
};

// Server titles carry their own emoji ("💤 Going quiet…"); the panel shows the kind icon
// in its own column, so strip a leading emoji from the title to avoid showing it twice.
const stripLeadEmoji = (s) =>
  (s || "").replace(/^[\p{Extended_Pictographic}\u{FE0F}\u{200D}\u{20E3}]+\s*/u, "");

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function NotificationBell({ collapsed }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  // The panel is PORTALLED to <body> with fixed positioning: the sidebar creates a
  // stacking context (same trap as the call-card hover popover, 2026-08-21), so an
  // absolutely-positioned child can never paint above the main content.
  const [pos, setPos] = useState(null);
  const navigate = useNavigate();
  const boxRef = useRef(null);
  const panelRef = useRef(null);

  const openPanel = () => {
    const r0 = boxRef.current?.getBoundingClientRect();
    if (r0) {
      // Screen px → layout px (the page may be display-scaled; see scale.js).
      const z = uiZoom();
      const r = { right: r0.right / z, bottom: r0.bottom / z };
      const vp = layoutViewport();
      const width = Math.min(380, vp.w - r.right - 24);
      setPos({
        left: r.right + 10,
        bottom: Math.max(12, vp.h - r.bottom),
        width: width > 260 ? width : 340,
        maxHeight: Math.min(520, r.bottom - 12),
      });
    }
    setOpen(true);
    load();
    // Focus the panel so arrow keys / PageDown scroll the list, not the page behind it.
    requestAnimationFrame(() => panelRef.current?.focus());
  };

  const load = () => {
    if (document.visibilityState === "hidden") return;
    api.get("/api/notifications?limit=30")
      .then((d) => { setItems(d.items || []); setUnread(d.unreadCount || 0); })
      .catch(() => {});
  };
  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  // Click-away, scroll and resize all close the panel (it's fixed-position, so a scroll
  // would leave it floating detached from the bell).
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (boxRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onResize = () => setOpen(false);
    // Close when the PAGE scrolls (the fixed panel would detach from the bell) — but a
    // scroll INSIDE the panel is just reading the list and must never close it.
    const onScroll = (e) => {
      if (panelRef.current && e.target instanceof Node && panelRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open]);

  const openOne = async (n) => {
    // A cancelled order is acknowledged on the Today page, not by opening it here (Salem 2026-11-02).
    if (n.kind === "order_cancelled" || n.kind === "order_sov_changed") { setOpen(false); navigate("/today"); return; }
    if (!n.read) {
      setItems((xs) => xs.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
      api.post(`/api/notifications/${n.id}/read`, {}).catch(() => {});
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };
  const readAll = () => {
    setItems((xs) => xs.map((x) => (ORDER_NOTICE.has(x.kind) ? x : { ...x, read: true })));
    setUnread(items.filter((x) => ORDER_NOTICE.has(x.kind) && !x.read).length);   // they wait for an acknowledgement
    api.post("/api/notifications/read-all", {}).catch(() => {});
  };

  return (
    <div className="bell-wrap" ref={boxRef}>
      <button className="sidebar-link bell-btn" title="Notifications"
        onClick={() => (open ? setOpen(false) : openPanel())}>
        <span className="sidebar-icon" aria-hidden="true" style={{ position: "relative" }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.7 21a2 2 0 0 1-3.4 0" />
          </svg>
          {unread > 0 && <span className="bell-badge">{unread > 9 ? "9+" : unread}</span>}
        </span>
        <span className="sidebar-label">Notifications{unread > 0 ? ` (${unread})` : ""}</span>
      </button>
      {open && pos && createPortal(
        <div className="bell-panel" ref={panelRef} tabIndex={-1}
          style={{ left: pos.left, bottom: pos.bottom, width: pos.width, maxHeight: pos.maxHeight, outline: "none" }}>
          <div className="bell-panel-head">
            <b>Notifications</b>
            {unread > 0 && <button className="bell-readall" onClick={readAll}>Mark all read</button>}
          </div>
          {items.length === 0 && (
            <div className="bell-empty">Nothing yet — reminders and lead updates land here.</div>
          )}
          {items.map((n) => (
            <button key={n.id} className={`bell-item ${n.read ? "" : "bell-item-unread"}`}
              onClick={() => openOne(n)}>
              <span className="bell-item-icon">{KIND_ICON[n.kind] || "🔔"}</span>
              <span className="bell-item-body">
                <span className="bell-item-title">{stripLeadEmoji(n.title)}</span>
                {n.body && <span className="bell-item-text">{n.body}</span>}
                <span className="bell-item-time">{timeAgo(n.at)}</span>
              </span>
              {!n.read && <span className="bell-item-dot" aria-hidden="true" />}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}
